import type { LatLng } from 'shared/lib/tracking/geo';
import { routeProgress } from 'shared/lib/tracking/geo';
import DriverMarkerIcon from 'features/tracking/components/DriverMarkerIcon';
import TrackingMapOverlay from 'features/tracking/components/TrackingMapOverlay';
import {
  SIMPLE_ROUTE_SCREEN_BEARING,
  type TrackingVehicleType,
} from 'features/tracking/lib/vehicleMarker';

interface SimpleRouteTrackerProps {
  pickup: LatLng;
  delivery: LatLng;
  driverPosition: LatLng | null;
  bearing: number;
  vehicleType?: TrackingVehicleType;
  isStale: boolean;
  lastUpdated: string | null;
  pickupLabel: string;
  deliveryLabel: string;
  waitingForDriver: boolean;
}

export default function SimpleRouteTracker({
  pickup,
  delivery,
  driverPosition,
  bearing: _bearing,
  vehicleType = 'car',
  isStale,
  lastUpdated,
  pickupLabel,
  deliveryLabel,
  waitingForDriver,
}: SimpleRouteTrackerProps) {
  const progress = driverPosition
    ? routeProgress(pickup, delivery, driverPosition)
    : 0;
  const pct = Math.round(progress * 100);

  /** Icon follows the drawn diagonal, not GPS heading (0° while stationary looks wrong). */
  const displayBearing = SIMPLE_ROUTE_SCREEN_BEARING;

  return (
    <div className="relative h-[320px] rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-primary/5 border border-slate-100">
      <TrackingMapOverlay
        isLive={Boolean(driverPosition)}
        isStale={isStale}
        lastUpdated={lastUpdated}
      />

      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 400 200"
        preserveAspectRatio="none"
        aria-hidden
      >
        <line
          x1="40"
          y1="160"
          x2="360"
          y2="40"
          stroke="#e2e8f0"
          strokeWidth="6"
          strokeLinecap="round"
        />
        <line
          x1="40"
          y1="160"
          x2={40 + 320 * progress}
          y2={160 - 120 * progress}
          stroke="var(--color-primary, #f97316)"
          strokeWidth="6"
          strokeLinecap="round"
          opacity={driverPosition ? 1 : 0.3}
        />
      </svg>

      <div className="absolute bottom-6 left-6 max-w-[45%]">
        <div className="w-3 h-3 rounded-full bg-primary mb-1 shadow-md" />
        <p className="text-[10px] font-bold text-slate-500 uppercase">Pickup</p>
        <p className="text-xs font-medium text-slate-700 line-clamp-2">
          {pickupLabel}
        </p>
      </div>

      <div className="absolute top-6 right-6 max-w-[45%] text-right">
        <div className="w-3 h-3 rounded-full bg-green-500 mb-1 ml-auto shadow-md" />
        <p className="text-[10px] font-bold text-slate-500 uppercase">Delivery</p>
        <p className="text-xs font-medium text-slate-700 line-clamp-2">
          {deliveryLabel}
        </p>
      </div>

      {waitingForDriver ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-sm font-medium text-slate-500 bg-white/90 px-4 py-2 rounded-xl shadow-sm">
            Waiting for driver location…
          </p>
        </div>
      ) : driverPosition ? (
        <div
          className="absolute transition-all duration-300 ease-out"
          style={{
            left: `${8 + pct * 0.84}%`,
            top: `${78 - pct * 0.38}%`,
            transform: `translate(-50%, -50%) rotate(${displayBearing}deg)`,
          }}
        >
          <DriverMarkerIcon
            bearing={0}
            vehicleType={vehicleType}
            variant="route"
          />
        </div>
      ) : null}
    </div>
  );
}
